/**
 * USE DONATION STATS HOOK
 * 
 * Computes statistics from donation data.
 * 
 * @module Donations/Hooks/useDonationStats
 */

import { useMemo } from 'react';
import { Donation, DonationCategory, DonationStatus } from '../types/donation.types';
import { DONATION_CATEGORIES } from '../constants/donation.constants';

interface UseDonationStatsReturn {
    totalDonations: number;
    totalAmount: number;
    averageAmount: number;
    byCategory: Record<string, number>;
    byStatus: Record<string, number>;
    categoryPercentages: Record<string, number>;
    monthlyTrends: Array<{ month: string; amount: number; count: number }>;
    completionRate: number;
}

export const useDonationStats = (donations: Donation[]): UseDonationStatsReturn => {
    return useMemo(() => {
        if (!Array.isArray(donations) || donations.length === 0) {
            return {
                totalDonations: 0,
                totalAmount: 0,
                averageAmount: 0,
                byCategory: {},
                byStatus: {},
                categoryPercentages: {},
                monthlyTrends: [],
                completionRate: 0
            };
        }

        // Total donations and amount
        const totalDonations = donations.length;
        const totalAmount = donations.reduce((sum, d) => sum + d.amount, 0);
        const averageAmount = totalDonations > 0 ? totalAmount / totalDonations : 0;

        // By category
        const byCategory: Record<string, number> = {};
        DONATION_CATEGORIES.forEach(cat => {
            byCategory[cat.value] = donations
                .filter(d => d.category === cat.value)
                .reduce((sum, d) => sum + d.amount, 0);
        });

        // By status
        const byStatus: Record<string, number> = {};
        Object.values(DonationStatus).forEach(status => {
            byStatus[status] = donations
                .filter(d => d.status === status)
                .length;
        });

        // Category percentages
        const categoryPercentages: Record<string, number> = {};
        if (totalAmount > 0) {
            Object.keys(byCategory).forEach(key => {
                categoryPercentages[key] = (byCategory[key] / totalAmount) * 100;
            });
        }

        // Monthly trends
        const monthlyMap: Record<string, { amount: number; count: number }> = {};
        donations.forEach(d => {
            const date = new Date(d.createdAt);
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!monthlyMap[key]) {
                monthlyMap[key] = { amount: 0, count: 0 };
            }
            monthlyMap[key].amount += d.amount;
            monthlyMap[key].count += 1;
        });

        const monthlyTrends = Object.entries(monthlyMap)
            .map(([month, data]) => ({ month, ...data }))
            .sort((a, b) => a.month.localeCompare(b.month));

        // Completion rate (completed vs total)
        const completed = byStatus[DonationStatus.COMPLETED] || 0;
        const total = totalDonations;
        const completionRate = total > 0 ? (completed / total) * 100 : 0;

        return {
            totalDonations,
            totalAmount,
            averageAmount,
            byCategory,
            byStatus,
            categoryPercentages,
            monthlyTrends,
            completionRate
        };
    }, [donations]);
};
